import React, { FC, forwardRef, ReactNode } from "react";
import {
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  MenuItem as MuiMenuItem,
  MenuItemProps,
  FormControl,
  InputLabel,
  Box,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { COLORS, FONTS, RADII, FONT_FAMILIES, FONT_WEIGHTS, SIZES } from "../../common/consts";

import { ReactComponent as SelectDown } from "../../assets/icons/select-down.svg";

export interface SelectOption {
  value: string;
  label?: ReactNode;
  disabled?: boolean;
}

// `notched` is not declared on SelectProps but Select spreads unknown props onto
// the OutlinedInput it renders, which is where the outline legend lives.
type BaseSelectProps = Omit<MuiSelectProps<string | string[]>, "options"> & {
  notched?: boolean;
};

interface SelectProps extends BaseSelectProps {
  options: SelectOption[];
  variant?: "outlined" | "filled" | "standard";
  value?: string | string[];
}

const SelectBase = forwardRef<unknown, BaseSelectProps>(
  function SelectBase(props, ref) {
    return <MuiSelect {...(props as MuiSelectProps<string | string[]>)} ref={ref} />;
  }
);
SelectBase.displayName = "SelectBase";

const MenuItemBase = forwardRef<HTMLLIElement, MenuItemProps>(
  function MenuItemBase(props, ref) {
    return <MuiMenuItem {...props} ref={ref} />;
  }
);
MenuItemBase.displayName = "MenuItemBase";

const StyledInputLabel = styled(InputLabel)`
  && {
    /* No background chip: the outline below renders a real notch (legend) for
       the label, so painting a background here would only mismatch the field
       fill and read as a floating rectangle over the border. */
    color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary}) !important;
    font-family: ${FONT_FAMILIES.regular} !important;
    font-weight: ${FONT_WEIGHTS.light} !important;
    font-size: ${SIZES.inputLabelFontSize} !important;
  }
  
  &&.Mui-focused {
    color: var(--color-inputLabel, ${COLORS.inputLabel}) !important;
  }
  
  &&.MuiInputLabel-shrink {
    color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary}) !important;
  }
`;

const StyledMenuItem = styled(MenuItemBase)`
  && {
    font: ${FONTS.sm};
    color: var(--color-foregroundTertiary, ${COLORS.foregroundTertiary});
    background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
    &.Mui-selected {
      background: var(--color-backgroundBrandLight, ${COLORS.backgroundBrandLight});
    }
    &:hover {
      background-color: var(--color-backgroundSecondary, ${COLORS.backgroundSecondary});
    }
  }
`;

/* Geometry is deliberately identical to Input's outlined TextField (same fill,
   padding, font and line-height) so a Select never renders shorter than the
   text fields it sits next to. The border lives on the notched outline — not
   on the root — so the label gets a real gap in the border instead of being
   covered by a painted background. */
const StyledSelect = styled(SelectBase)<{ fullWidth?: boolean }>`
  && {
    border-radius: ${RADII.md};
    font: ${FONTS.sm};
    color: var(--color-foregroundTertiary, ${COLORS.foregroundTertiary});
    background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
    width: ${props => props.fullWidth ? '100%' : 'auto'};
    min-width: ${props => props.fullWidth ? '100%' : '120px'};

    & .MuiOutlinedInput-notchedOutline {
      border: 1px solid var(--color-inputOutline, ${COLORS.inputOutline});
      border-radius: ${RADII.md};
    }
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
      border-width: 1px;
    }
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
      border-width: 1px;
    }
  }

  && .MuiSelect-select {
    min-height: unset;
    box-sizing: border-box;
    padding: 8px 32px 8px 16px;
    font-family: ${FONT_FAMILIES.regular};
    font-size: ${SIZES.inputFieldFontSize};
    font-weight: ${SIZES.inputFieldFontWeight};
    /* 8 + 20 + 8 gives Outreach's 36px control height exactly. */
    line-height: 20px;
  }

  .MuiSvgIcon-root {
    color: var(--color-dropdownIconColor, ${COLORS.dropdownIconColor});
  }

  .MuiSelect-icon {
    /* Truly centre the chevron regardless of icon intrinsic size. */
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-dropdownIconColor, ${COLORS.dropdownIconColor});
  }

  .MuiSelect-icon.MuiSelect-iconOpen {
    transform: translateY(-50%) rotate(180deg);
  }
`;

const StyledChipsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 4;
`;

const Select: FC<SelectProps> = ({
  options,
  label,
  multiple,
  renderValue,
  value,
  ...rest
}) => {
  const hasValue = value !== undefined && value !== null && value !== "";
  const isArrayValue = Array.isArray(value);
  const hasArrayValue = isArrayValue && value.length > 0;
  const isShrunk = hasValue || hasArrayValue;

  return (
    <FormControl
      size="small"
      fullWidth={rest.fullWidth}
      sx={rest.fullWidth ? {} : { width: 'auto', display: 'inline-block' }}
    >
      {label && (
        <StyledInputLabel shrink={isShrunk}>
          {label}
        </StyledInputLabel>
      )}
      <StyledSelect
        {...rest}
        // `label` sizes the outline's legend and `notched` opens it in step with
        // the floating label above — without both, the label would sit on top of
        // an unbroken border.
        label={label}
        notched={isShrunk}
        value={value}
        multiple={multiple}
        fullWidth={rest.fullWidth}
        renderValue={(selected) => {
          if (renderValue) {
            return renderValue(selected as string | string[]);
          }
          if (multiple) {
            const selectedValues = selected as string[];
            return (
              <StyledChipsContainer>
                {selectedValues.map((val) => {
                  const option = options?.find((opt) => opt.value === val);
                  return (
                    <Chip
                      key={val}
                      label={option?.label || val}
                      size="small"
                      sx={{ font: FONTS.sm }}
                    />
                  );
                })}
              </StyledChipsContainer>
            );
          }
          const selectedValue = selected as string;
          const option = options?.find((opt) => opt.value === selectedValue);
          return option?.label || selectedValue;
        }}
        IconComponent={SelectDown}
      >
        {options && options.map((option) => (
          <StyledMenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label || option.value}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </FormControl>
  );
};

export default Select;
